import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from './Loading';
import Post from './Post';
import { trackInternalNavigationIfConsented, usePageGroupTracking } from './pageAnalytics';
import { fetchPostById, fetchRecentPosts } from './postData';
import {
  getPostIdFromSetupSlug,
  getRelatedPosts,
  getSetupPageData,
  parseListTags,
} from './setupContent';
import { usePageSeo } from './seo';

const SetupPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const postId = getPostIdFromSetupSlug(slug);
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);

  const setup = useMemo(() => (post ? getSetupPageData(post) : null), [post]);

  usePageSeo({
    title: setup?.title || 'Desk setup',
    description: setup?.description || 'Browse a real desk setup and discover related workspace inspiration on 101Setups.',
    canonicalPath: setup?.path || location.pathname,
    type: 'article',
    robots: post ? 'index,follow' : 'noindex,nofollow',
  });
  usePageGroupTracking('setup_page', {
    primary_topic: setup?.primaryCategory?.slug || 'unknown',
  });

  useEffect(() => {
    let isMounted = true;

    const loadPost = async () => {
      setLoading(true);
      const [fetchedPost, recent] = await Promise.all([fetchPostById(postId), fetchRecentPosts(48)]);

      if (!isMounted) {
        return;
      }

      setPost(fetchedPost);
      setRecentPosts(recent);
      setLoading(false);
    };

    loadPost();

    return () => {
      isMounted = false;
    };
  }, [postId]);

  useEffect(() => {
    if (setup?.path && setup.path !== location.pathname) {
      navigate(setup.path, { replace: true });
    }
  }, [location.pathname, navigate, setup?.path]);

  if (loading) {
    return <Loading />;
  }

  if (!post || !setup) {
    return (
      <section className="page-shell">
        <div className="empty-state">
          <h1>Setup not found</h1>
          <p>The setup may have been removed or its route has changed.</p>
          <Link className="home-hero-link primary" to="/latest">
            Browse latest setups
          </Link>
        </div>
      </section>
    );
  }

  const relatedPosts = getRelatedPosts(recentPosts, post, 3);

  return (
    <section className="page-shell">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link
          to="/"
          onClick={() =>
            trackInternalNavigationIfConsented({
              link_target: '/',
              link_context: 'setup_breadcrumb_home',
              source_page_group: 'setup_page',
              destination_page_group: 'home',
            })
          }
        >
          Home
        </Link>
        <span>/</span>
        <Link
          to="/desk-setup-ideas"
          onClick={() =>
            trackInternalNavigationIfConsented({
              link_target: '/desk-setup-ideas',
              link_context: 'setup_breadcrumb_search_hub',
              source_page_group: 'setup_page',
              destination_page_group: 'search_hub',
            })
          }
        >
          Desk setup ideas
        </Link>
        {setup.primaryCategory ? (
          <>
            <span>/</span>
            <Link
              to={`/setup-ideas/${setup.primaryCategory.slug}`}
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: `/setup-ideas/${setup.primaryCategory.slug}`,
                  link_context: 'setup_breadcrumb_topic_hub',
                  source_page_group: 'setup_page',
                  destination_page_group: 'topic_hub',
                })
              }
            >
              {setup.primaryCategory.shortLabel}
            </Link>
          </>
        ) : null}
      </nav>

      <header className="page-intro setup-detail-intro">
        <p className="page-kicker">Real setup page</p>
        <h1>{setup.title}</h1>
        <p>{setup.excerpt}</p>
        <div className="topic-chip-row">
          {setup.primaryCategory ? (
            <Link
              className="topic-chip"
              to={`/setup-ideas/${setup.primaryCategory.slug}`}
              onClick={() =>
                trackInternalNavigationIfConsented({
                  link_target: `/setup-ideas/${setup.primaryCategory.slug}`,
                  link_context: 'setup_topic_chip',
                  source_page_group: 'setup_page',
                  destination_page_group: 'topic_hub',
                })
              }
            >
              {setup.primaryCategory.shortLabel}
            </Link>
          ) : null}
          <span className="topic-chip">By {setup.creator}</span>
          {setup.categories.slice(0, 3).map(category => (
            <span className="topic-chip" key={category}>
              {category}
            </span>
          ))}
        </div>
      </header>

      <div className="page-section">
        <Post
          id={post.id}
          userID={post.userid}
          imgURL={post.imgURL}
          listTags={parseListTags(post.listtags)}
          title={setup.title}
          summary={setup.excerpt}
          categoryLabel={setup.primaryCategory?.shortLabel}
          canonicalPath={setup.path}
          sharePath={setup.path}
          altText={setup.altText}
          showViewLink={false}
          pageContext="setup_page"
        />
      </div>

      <div className="content-grid-two page-section">
        <article className="content-card">
          <h2>What stands out</h2>
          <p>{setup.story}</p>
        </article>
        <article className="content-card">
          <h2>Explore next</h2>
          <ul className="page-summary-list">
            {setup.primaryCategory ? (
              <li>
                <Link
                  to={`/setup-ideas/${setup.primaryCategory.slug}`}
                  onClick={() =>
                    trackInternalNavigationIfConsented({
                      link_target: `/setup-ideas/${setup.primaryCategory.slug}`,
                      link_context: 'setup_explore_more_topic',
                      source_page_group: 'setup_page',
                      destination_page_group: 'topic_hub',
                    })
                  }
                >
                  More {setup.primaryCategory.shortLabel.toLowerCase()} setup ideas
                </Link>
              </li>
            ) : null}
            <li>
              <Link
                to="/latest"
                onClick={() =>
                  trackInternalNavigationIfConsented({
                    link_target: '/latest',
                    link_context: 'setup_explore_latest',
                    source_page_group: 'setup_page',
                    destination_page_group: 'latest_page',
                  })
                }
              >
                Latest desk setups
              </Link>
            </li>
            <li>
              <a
                href="/#newsletter"
                onClick={() =>
                  trackInternalNavigationIfConsented({
                    link_target: '/#newsletter',
                    link_context: 'setup_explore_newsletter',
                    source_page_group: 'setup_page',
                    destination_page_group: 'footer_newsletter',
                  })
                }
              >
                Get the newsletter
              </a>
            </li>
          </ul>
        </article>
      </div>

      {relatedPosts.length > 0 ? (
        <div className="page-section">
          <div className="section-heading-block">
            <p className="page-kicker">Related setup ideas</p>
            <h2>Keep browsing</h2>
          </div>
          <div className="post-list-grid compact-grid">
            {relatedPosts.map(relatedPost => {
              const relatedSetup = getSetupPageData(relatedPost);

              return (
                <Post
                  key={relatedPost.id}
                  id={relatedPost.id}
                  userID={relatedPost.userid}
                  imgURL={relatedPost.imgURL}
                  listTags={parseListTags(relatedPost.listtags)}
                  title={relatedSetup.title}
                  summary={relatedSetup.excerpt}
                  categoryLabel={relatedSetup.primaryCategory?.shortLabel}
                  canonicalPath={relatedSetup.path}
                  sharePath={relatedSetup.path}
                  altText={relatedSetup.altText}
                  pageContext="setup_related"
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SetupPage;
